import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { emojify } from "node-emoji";
import "./Login.css";
import config from "../config";
import logo_img from "../public/img/LT_logo.svg";
import { useAuth } from "../contexts/AuthContext";

// Skin tone modifier map
const skinToneMap = {
  ":skin-tone-2:": "🏻", // light skin tone
  ":skin-tone-3:": "🏼", // medium-light skin tone
  ":skin-tone-4:": "🏽", // medium skin tone
  ":skin-tone-5:": "🏾", // medium-dark skin tone
  ":skin-tone-6:": "🏿", // dark skin tone
};

// Function to process HTML content and convert emojis
const processMessageContent = (htmlContent) => {
  if (!htmlContent) return htmlContent;

  // First, handle skin tone modifiers
  let processedContent = handleSkinToneModifiers(htmlContent);

  // Then use node-emoji to convert remaining emoji codes
  return emojify(processedContent);
};

// Function to handle skin tone modifiers in text
const handleSkinToneModifiers = (text) => {
  if (!text) return text;

  // Regular expression to find emoji codes with skin tone modifiers
  // This looks for patterns like :wave::skin-tone-2:
  const skinToneRegex = /:([\w-]+):(:skin-tone-[2-6]:)/g;

  // Replace emoji codes with skin tone modifiers
  return text.replace(skinToneRegex, (match, emojiName, skinTone) => {
    // First convert the base emoji
    const baseEmoji = emojify(`:${emojiName}:`);

    // If it's not a real emoji or doesn't support skin tones, return as is
    if (baseEmoji === `:${emojiName}:` || !canHaveSkinTone(baseEmoji)) {
      return match;
    }

    // Get the skin tone modifier
    const skinToneModifier = skinToneMap[skinTone] || "";

    // Combine the base emoji with the skin tone modifier
    return baseEmoji + skinToneModifier;
  });
};

// Function to check if an emoji can have a skin tone
// This is a simplified check - a more comprehensive solution would use a full list
const canHaveSkinTone = (emoji) => {
  // List of emoji categories that typically support skin tones
  const skinToneEmojiRegex =
    /[👋👌👍👎👊✊🤛🤜👏👐🙌🤲🙏✍️💅🤳💪🦵🦶👂👃🧠🦷🦴👀👁️👅👄]/;
  return skinToneEmojiRegex.test(emoji);
};

// MadProps component with easter egg
const MadProps = ({ sectionRef }) => {
  const [allMessages, setAllMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animationState, setAnimationState] = useState("idle");

  // Easter egg state
  const [easterEggActive, setEasterEggActive] = useState(false);
  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const velocityRef = useRef({ x: 2, y: 1.5 }); // Initial velocity
  const animationFrameRef = useRef(null);
  const contentFadeRef = useRef(null);

  // Add a ref to track the current color
  const currentColorRef = useRef(null);

  // Function to select a random message from the cached messages
  const selectRandomMessage = useCallback(() => {
    if (allMessages && allMessages.length > 0) {
      // Find a different message than the current one
      let newIndex;
      let attempts = 0;
      do {
        newIndex = Math.floor(Math.random() * allMessages.length);
        attempts++;
      } while (
        allMessages[newIndex]?.user?.real_name ===
          currentMessage?.user?.real_name &&
        attempts < 10
      );

      if (easterEggActive) {
        // Fade out content
        if (contentFadeRef.current) {
          contentFadeRef.current.style.opacity = "0";

          // After fade out, update content and fade in
          setTimeout(() => {
            setCurrentMessage(allMessages[newIndex]);

            // After content update, fade in
            setTimeout(() => {
              if (contentFadeRef.current) {
                contentFadeRef.current.style.opacity = "1";
              }
            }, 50);
          }, 500);
        } else {
          setCurrentMessage(allMessages[newIndex]);
        }
      } else {
        // Normal fade animations for the whole card
        setAnimationState("exiting");

        setTimeout(() => {
          setCurrentMessage(allMessages[newIndex]);
          setAnimationState("entering");

          setTimeout(() => {
            setAnimationState("idle");
          }, 700);
        }, 700);
      }
    }
  }, [
    allMessages,
    currentMessage,
    easterEggActive,
    setAnimationState,
    setCurrentMessage,
  ]);

  // Debug function to check if double-click is being registered
  const handleDoubleClick = (e) => {
    console.log("Double click detected!");

    // Toggle easter egg mode
    setEasterEggActive((prevState) => {
      const newState = !prevState;
      console.log("Easter egg active:", newState);

      if (newState) {
        // Activate easter egg mode
        startBouncing();
      } else {
        // Deactivate easter egg mode
        stopBouncing();
      }

      return newState;
    });
  };

  // Start the bouncing animation
  const startBouncing = () => {
    if (cardRef.current) {
      console.log("Starting bounce animation");

      // Set random initial velocity with a good angle
      const randomDirection = Math.random() * Math.PI * 2;
      const speed = 1;

      // Ensure we have both x and y components with sufficient magnitude
      let vx = Math.cos(randomDirection) * speed;
      let vy = Math.sin(randomDirection) * speed;

      // Make sure neither component is too small (avoid mostly vertical/horizontal movement)
      if (Math.abs(vx) < 1) vx = vx > 0 ? 1 : -1;
      if (Math.abs(vy) < 1) vy = vy > 0 ? 1 : -1;

      velocityRef.current = { x: vx, y: vy };

      // Hide the white blur gradient
      const contentElement =
        cardRef.current.querySelector(".mad-props-content");
      if (contentElement) {
        contentElement.classList.add("easter-egg-mode");
      }

      // Start with a random color
      changeCardColor();

      // Start the animation
      animationFrameRef.current = requestAnimationFrame(updateCardPosition);
    }
  };

  // Stop the bouncing animation
  const stopBouncing = () => {
    console.log("Stopping bounce animation");

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Reset card position and styles
    if (cardRef.current) {
      cardRef.current.style.transform = "";
      cardRef.current.style.backgroundColor = ""; // Remove inline style to use CSS variable
      cardRef.current.style.boxShadow = ""; // Remove inline style to use CSS variable

      // Reset text color and show the blur gradient again
      const contentElement =
        cardRef.current.querySelector(".mad-props-content");
      if (contentElement) {
        contentElement.style.color = ""; // Remove inline style to use CSS variable
        contentElement.classList.remove("easter-egg-mode");
      }

      // Reset the reaction dots styling
      const reactionElements = cardRef.current.querySelectorAll(".reaction");
      if (reactionElements.length > 0) {
        reactionElements.forEach((reaction) => {
          reaction.style.backgroundColor = ""; // Remove inline style to use CSS variable
          const countElement = reaction.querySelector(".reaction-count");
          if (countElement) {
            countElement.style.color = ""; // Remove inline style to use CSS variable
          }
        });
      }

      // Reset the current color
      currentColorRef.current = null;
    }
  };

  // DVD logo style bouncing animation
  const updateCardPosition = () => {
    if (!cardRef.current || !sectionRef.current) {
      return;
    }

    // Get the actual visible section dimensions
    const sectionRect = sectionRef.current.getBoundingClientRect();
    const cardRect = cardRef.current.getBoundingClientRect();

    // Get current position from transform or default to 0,0
    let currentX = 0;
    let currentY = 0;

    const transform = cardRef.current.style.transform;
    if (transform) {
      const match = transform.match(
        /translate\((-?\d+\.?\d*)px, (-?\d+\.?\d*)px\)/
      );
      if (match) {
        currentX = parseFloat(match[1]);
        currentY = parseFloat(match[2]);
      }
    }

    // Update position
    currentX += velocityRef.current.x;
    currentY += velocityRef.current.y;

    // Calculate the bounds relative to the section
    // We need to convert from global coordinates to section-relative coordinates
    const cardLeft = cardRect.left - sectionRect.left;
    const cardTop = cardRect.top - sectionRect.top;

    // Calculate the maximum positions
    const maxX = sectionRect.width - cardRect.width;
    const maxY = sectionRect.height - cardRect.height;

    // Check boundaries and bounce
    let hitWall = false;

    // Horizontal bounds check
    if (cardLeft + velocityRef.current.x < 0) {
      // We're about to go past the left edge
      currentX = currentX - cardLeft; // Adjust to exactly the left edge
      velocityRef.current.x = Math.abs(velocityRef.current.x);
      hitWall = true;
    } else if (cardLeft + velocityRef.current.x > maxX) {
      // We're about to go past the right edge
      currentX = currentX + (maxX - cardLeft); // Adjust to exactly the right edge
      velocityRef.current.x = -Math.abs(velocityRef.current.x);
      hitWall = true;
    }

    // Vertical bounds check
    if (cardTop + velocityRef.current.y < 0) {
      // We're about to go past the top edge
      currentY = currentY - cardTop; // Adjust to exactly the top edge
      velocityRef.current.y = Math.abs(velocityRef.current.y);
      hitWall = true;
    } else if (cardTop + velocityRef.current.y > maxY) {
      // We're about to go past the bottom edge
      currentY = currentY + (maxY - cardTop); // Adjust to exactly the bottom edge
      velocityRef.current.y = -Math.abs(velocityRef.current.y);
      hitWall = true;
    }

    // Only change color if we hit a wall
    if (hitWall) {
      changeCardColor();
    }

    // Apply the new position
    cardRef.current.style.transform = `translate(${currentX}px, ${currentY}px)`;

    // Continue the animation
    animationFrameRef.current = requestAnimationFrame(updateCardPosition);
  };

  // Change card color on bounce (DVD logo style)
  const changeCardColor = () => {
    if (cardRef.current) {
      const colors = [
        "#ff0000",
        "#00ff00",
        "#0000ff",
        "#ffff00",
        "#ff00ff",
        "#00ffff",
        "#ff8000",
        "#8000ff",
      ];

      // Filter out the current color to avoid picking it again
      const availableColors = currentColorRef.current
        ? colors.filter((color) => color !== currentColorRef.current)
        : colors;

      // Pick a random color from the available colors
      const randomColor =
        availableColors[Math.floor(Math.random() * availableColors.length)];

      // Store the current color for next time
      currentColorRef.current = randomColor;

      // Change box shadow with 50% opacity
      cardRef.current.style.boxShadow = `0 8px 24px ${randomColor}80`;

      // Change background color with 20% opacity for a subtle effect
      cardRef.current.style.backgroundColor = `${randomColor}33`;

      // Change text color to match the random color for better contrast
      const contentElement =
        cardRef.current.querySelector(".mad-props-content");
      if (contentElement) {
        // Make text color match the background for a cohesive look
        contentElement.style.color = `${randomColor}`;

        // Hide the white blur gradient
        contentElement.classList.add("easter-egg-mode");
      }

      // Change the background color of reaction dots
      const reactionElements = cardRef.current.querySelectorAll(".reaction");
      if (reactionElements.length > 0) {
        reactionElements.forEach((reaction) => {
          // Use a more solid version of the color (60% opacity)
          reaction.style.backgroundColor = `${randomColor}99`;
          // Change the text color to ensure readability
          const countElement = reaction.querySelector(".reaction-count");
          if (countElement) {
            countElement.style.color = "#ffffff";
          }
        });
      }
    }
  };

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Fetch all messages once
  useEffect(() => {
    const fetchMadProps = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.slack.madProps.all}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch mad props");
        }

        const data = await response.json();

        if (data.messages && data.messages.length > 0) {
          setAllMessages(data.messages);
          // Select an initial random message
          const randomIndex = Math.floor(Math.random() * data.messages.length);
          setCurrentMessage(data.messages[randomIndex]);
        } else {
          throw new Error("No messages found");
        }

        setError(null);
      } catch (err) {
        setError("Failed to load mad props. Please try again later.");
        console.error("Error fetching mad props:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMadProps();
  }, []);

  // Set up interval to select a new random message every few seconds
  useEffect(() => {
    if (allMessages.length === 0) return;

    const intervalId = setInterval(() => {
      selectRandomMessage();
    }, 15000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [allMessages, animationState, currentMessage, selectRandomMessage]);

  // Helper function to render emojis correctly
  const renderEmoji = (emojiCode) => {
    // Check if the emoji is a URL (custom Slack emoji)
    if (emojiCode.startsWith("http")) {
      return <img src={emojiCode} alt="emoji" className="emoji-image" />;
    }

    // Check if this is an emoji with a skin tone modifier
    if (emojiCode.includes(":skin-tone-")) {
      // Split the emoji code into parts
      const parts = emojiCode.split(":");
      if (parts.length >= 4) {
        // Extract the base emoji name and skin tone
        const baseEmojiName = parts[1];
        const skinTone = `:skin-tone-${parts[3]}:`;

        // Convert the base emoji
        const baseEmoji = emojify(`:${baseEmojiName}:`);

        // If it's a valid emoji that can have skin tones
        if (baseEmoji !== `:${baseEmojiName}:` && canHaveSkinTone(baseEmoji)) {
          // Get the skin tone modifier
          const skinToneModifier = skinToneMap[skinTone] || "";

          // Return the combined emoji
          return (
            <span>
              {baseEmoji}
              {skinToneModifier}
            </span>
          );
        }
      }
    }

    // For regular emojis, just use emojify
    return <span>{emojify(emojiCode)}</span>;
  };

  // Process the message content before rendering
  const processedContent = currentMessage
    ? processMessageContent(currentMessage.content)
    : "";

  if (loading && !currentMessage) {
    return <div className="mad-props-loading">Loading mad props...</div>;
  }

  if (error) {
    return <div className="mad-props-error">{error}</div>;
  }

  if (!currentMessage) {
    return <div className="mad-props-empty">No mad props available</div>;
  }

  return (
    <div className="mad-props-container" ref={containerRef}>
      <div className="mad-props-message-wrapper">
        {currentMessage && (
          <div
            ref={cardRef}
            className={`mad-props-message ${animationState} ${
              easterEggActive ? "easter-egg-active" : ""
            }`}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: "pointer" }}
          >
            <div
              ref={contentFadeRef}
              className={`mad-props-content-wrapper ${
                easterEggActive ? "easter-egg-content" : ""
              }`}
            >
              <div className="mad-props-user">
                <img
                  src={
                    currentMessage.user.image ||
                    "https://secure.gravatar.com/avatar/697ff15fe1db18780ed38cc4d86c3fe3.jpg?s=512&d=https%3A%2F%2Fa.slack-edge.com%2Fdf10d%2Fimg%2Favatars%2Fava_0003-512.png"
                  }
                  alt={currentMessage.user.real_name}
                  className="user-avatar"
                />
                <span className="user-name">
                  {currentMessage.user.real_name}
                </span>
              </div>
              <div
                className="mad-props-content"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
              <div className="mad-props-reactions">
                {currentMessage.reactions &&
                  currentMessage.reactions.map((reaction, index) => (
                    <span key={index} className="reaction">
                      {reaction.variants.map((variant, variantIndex) => (
                        <span key={variantIndex} className="emoji-variant">
                          {renderEmoji(variant.emoji)}
                        </span>
                      ))}
                      <span className="reaction-count">
                        {reaction.total_count}
                      </span>
                    </span>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  // Swipeable carousel state for mobile
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselRef = useRef(null);

  // Import the useAuth hook
  const { isAuthenticated, isCheckingAuth, login } = useAuth();

  // Check if user is already authenticated on page load
  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated && !isCheckingAuth) {
      const from =
        location.state && location.state.from ? location.state.from : null;
      const safePath = from
        ? `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`
        : "/directory?id=681a7c84791eb9a98d7f4660";
      navigate(safePath, { replace: true });
    }

    // Set local loading state based on context state
    setCheckingAuth(isCheckingAuth);
  }, [isAuthenticated, isCheckingAuth, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.auth.login}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ username: email, password }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Update the auth context with the logged-in user data
      await login(data);

      // Redirect to intended page (if any) on successful login
      const from =
        location.state && location.state.from ? location.state.from : null;
      const safePath = from
        ? `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`
        : "/directory?id=681a7c84791eb9a98d7f4660";
      navigate(safePath, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Swipe handlers for mobile carousel
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < 1) {
      setCurrentSlide(1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(0);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  // Mouse drag handlers for desktop on mobile view
  const handleMouseDown = (e) => {
    setTouchStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (touchStart) {
      setTouchEnd(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (!touchStart || !touchEnd) {
      setTouchStart(0);
      setTouchEnd(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < 1) {
      setCurrentSlide(1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(0);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  // Show loading indicator while checking authentication
  if (checkingAuth) {
    return (
      <div className="login-page">
        <div className="auth-checking">
          <div className="auth-checking-spinner"></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-logo-corner">
          <img src={logo_img} alt="LT Logo" className="lt-logo-corner" />
        </div>

        {/* Original desktop layout - visible on 993px+ */}
        <div className="login-form-container login-desktop-view">
          <div className="login-status">
            <span className="status-indicator"></span>
            <span className="status-text">Online now</span>
          </div>
          <div className="login-title-container">
            <h1 className="login-title">Welcome to the LT AI Hub</h1>
            <h1 className="login-title">Your AI assistant for work</h1>
          </div>

          {/* Fixed-height container for error messages */}
          <div className="error-message-container">
            {error && <div className="error-message">{error}</div>}
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-form-group">
              <input
                type="email"
                id="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="login-form-group">
              <input
                type="password"
                id="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Continue"}
            </button>
          </form>
          <p className="login-info">
            Login with your TargetProcess credentials.
          </p>
        </div>

        <div className="mad-props-section login-desktop-view" ref={sectionRef}>
          <video
            ref={videoRef}
            className="video-background"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/Login_BG.mp4" type="video/mp4" />
          </video>
          <MadProps sectionRef={sectionRef} />
        </div>

        {/* Mobile layout - visible on 992px and below */}
        <div className="login-mobile-container">
          {/* Static video background for mobile */}
          <div className="login-mobile-video-wrapper">
            <video
              className="video-background-mobile"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src="/Login_BG.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Carousel wrapper on top of video */}
          <div
            className="login-carousel-wrapper"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div
              className="login-carousel-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {/* Slide 1: Login Form */}
              <div className="login-carousel-slide">
                <div className="login-form-container login-mobile-form">
                  {/* Logo inside mobile card */}
                  <div className="login-mobile-logo">
                    <img
                      src={logo_img}
                      alt="LT Logo"
                      className="lt-logo-mobile"
                    />
                  </div>
                  <div className="login-status">
                    <span className="status-indicator"></span>
                    <span className="status-text">Online now</span>
                  </div>
                  <div className="login-title-container">
                    <h1 className="login-title">Welcome to the LT AI Hub</h1>
                    <h1 className="login-title">Your AI assistant for work</h1>
                  </div>

                  {/* Fixed-height container for error messages */}
                  <div className="error-message-container">
                    {error && <div className="error-message">{error}</div>}
                  </div>

                  <form className="login-form" onSubmit={handleSubmit}>
                    <div className="login-form-group">
                      <input
                        type="email"
                        id="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="login-form-group">
                      <input
                        type="password"
                        id="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="login-button"
                      disabled={loading}
                    >
                      {loading ? "Logging in..." : "Continue"}
                    </button>
                  </form>
                  <p className="login-info">
                    Login with your TargetProcess credentials.
                  </p>
                </div>
              </div>

              {/* Slide 2: Mad Props */}
              <div className="login-carousel-slide">
                <div className="mad-props-container-mobile">
                  <MadProps sectionRef={sectionRef} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel indicators for mobile */}
        <div className="login-carousel-indicators">
          <span
            className={`indicator ${currentSlide === 0 ? "active" : ""}`}
            onClick={() => setCurrentSlide(0)}
          ></span>
          <span
            className={`indicator ${currentSlide === 1 ? "active" : ""}`}
            onClick={() => setCurrentSlide(1)}
          ></span>
        </div>
      </div>
    </div>
  );
};

export default Login;
